import { join } from 'node:path';
import { fileURLToPath, pathToFileURL } from 'node:url';
import { optimizeLodashImports } from '@optimize-lodash/rollup-plugin';
import commonjs from '@rollup/plugin-commonjs';
import json from '@rollup/plugin-json';
import nodeResolve from '@rollup/plugin-node-resolve';
import { rollup } from 'rollup';
import type { InputOptions, OutputOptions, Plugin } from 'rollup';
import { minify as esbuildMinify } from 'rollup-plugin-esbuild';
import type { WorkspacePackageInfo } from '../bundler/workspaceDependencies';
import { esbuild } from './plugins/esbuild';
import { esmShim } from './plugins/esm-shim';
import { localStorageDetector } from './plugins/local-storage-detector';
import { nodeModulesExtensionResolver } from './plugins/node-modules-extension-resolver';
import { protocolExternalResolver } from './plugins/protocol-external-resolver';
import { removeDeployer } from './plugins/remove-deployer';
import { subpathExternalsResolver } from './plugins/subpath-externals-resolver';
import { tsConfigPaths } from './plugins/tsconfig-paths';
import type { ExternalDependencyInfo } from './types';
import { getNodeResolveOptions, slash } from './utils';
import type { BundlerPlatform } from './utils';

export function mastraInternalAliasPlugin(entryFile: string): Plugin {
  const normalizedEntryFile = slash(entryFile);

  return {
    name: 'mastra-internal-alias',
    resolveId: {
      order: 'pre',
      handler(id) {
        if (id === '#server') {
          return slash(fileURLToPath(import.meta.resolve('@mastra/deployer/server')));
        }

        if (id.startsWith('@mastra/server/')) {
          return fileURLToPath(import.meta.resolve(id));
        }

        if (id === '#mastra') {
          return normalizedEntryFile;
        }
      },
    },
  } satisfies Plugin;
}

export function mastraToolsAliasPlugin(): Plugin {
  return {
    name: 'tools-rewriter',
    resolveId(id: string) {
      if (id === '#tools') {
        return {
          id: './tools.mjs',
          external: true,
        };
      }
    },
  };
}

export async function getInputOptions(
  entryFile: string,
  analyzedBundleInfo: {
    dependencies: Map<string, string>;
    externalDependencies: Map<string, ExternalDependencyInfo>;
    workspaceMap: Map<string, WorkspacePackageInfo>;
    projectType?: string;
  },
  platform: BundlerPlatform,
  env: Record<string, string> = { 'process.env.NODE_ENV': JSON.stringify('production') },
  {
    sourcemap = false,
    minify = false,
    isDev = false,
    projectRoot,
    workspaceRoot = undefined,
    enableEsmShim = true,
    externalsPreset = false,
  }: {
    sourcemap?: boolean;
    minify?: boolean;
    isDev?: boolean;
    workspaceRoot?: string;
    projectRoot: string;
    enableEsmShim?: boolean;
    externalsPreset?: boolean;
  },
): Promise<InputOptions> {
  const nodeResolvePlugin = nodeResolve(getNodeResolveOptions(platform));

  const externalsCopy = new Set<string>(analyzedBundleInfo.externalDependencies.keys());
  const externals = externalsPreset ? [] : Array.from(externalsCopy);

  return {
    logLevel: process.env.MASTRA_BUNDLER_DEBUG === 'true' ? 'debug' : 'silent',
    treeshake: 'smallest',
    preserveSymlinks: true,
    external: externals,
    plugins: [
      protocolExternalResolver(),
      subpathExternalsResolver(externals),
      {
        name: 'alias-optimized-deps',
        resolveId(id: string) {
          if (!analyzedBundleInfo.dependencies.has(id)) {
            return null;
          }

          const filename = analyzedBundleInfo.dependencies.get(id)!;
          const absolutePath = join(workspaceRoot || projectRoot, filename);

          // During `mastra dev` we want to keep deps as external
          if (isDev) {
            return {
              id: process.platform === 'win32' ? pathToFileURL(absolutePath).href : absolutePath,
              external: true,
            };
          }

          // For production builds return the absolute path as-is so Rollup can handle itself
          return {
            id: absolutePath,
            external: false,
          };
        },
      } satisfies Plugin,
      mastraInternalAliasPlugin(entryFile),
      tsConfigPaths(),
      mastraToolsAliasPlugin(),
      esbuild({
        platform,
        define: env,
      }),
      optimizeLodashImports({
        include: '**/*.{js,ts,mjs,cjs}',
      }),
      externalsPreset
        ? null
        : commonjs({
            extensions: ['.js', '.ts'],
            transformMixedEsModules: true,
            esmExternals(id) {
              return externals.includes(id);
            },
          }),
      enableEsmShim ? esmShim() : undefined,
      externalsPreset ? nodeModulesExtensionResolver() : nodeResolvePlugin,
      // for debugging
      // {
      //   name: 'logger',
      //   //@ts-expect-error
      //   resolveId(id, ...args) {
      //     console.log({ id, args });
      //   },
      //   // @ts-expect-error
      // transform(code, id) {
      //   if (code.includes('class Duplexify ')) {
      //     console.log({ duplex: id });
      //   }
      // },
      // },
      json(),
      localStorageDetector(workspaceRoot || projectRoot),
      removeDeployer(entryFile, { sourcemap }),
      // treeshake unused imports
      esbuild({
        include: entryFile,
        platform,
      }),
      // Runs at renderChunk, so the emitted chunks are minified as a whole rather
      // than module by module. Last in the list so nothing transforms after it.
      // `sourceMap` follows the build's own setting: the plugin defaults it to true,
      // which would build a map Rollup then discards on a non-sourcemap build.
      minify ? esbuildMinify({ target: 'node20', sourceMap: sourcemap }) : null,
    ].filter(Boolean),
  } satisfies InputOptions;
}

export async function createBundler(
  inputOptions: InputOptions,
  outputOptions: Partial<OutputOptions> & { dir: string },
) {
  const bundler = await rollup(inputOptions);

  return {
    write: () => {
      return bundler.write({
        ...outputOptions,
        format: 'esm',
        entryFileNames: '[name].mjs',
        chunkFileNames: '[name].mjs',
      });
    },
    close: () => {
      return bundler.close();
    },
  };
}
